import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CmpsDialog, CmpsDialogRef } from '@cmps/ng-uikit';
import { Observable } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { FocusDto } from '../coaching-base/general';
import { CoachingSwitchboardService } from '../coaching-base/switchboard';
import { TabRefId } from '../coaching-detail';
import { FocusShallow } from '../coaching-tags/tags/tags.model';
import { takeUntilDestroy } from '../common/take-until-destroy.decorator';

import {
    ObservationPopUpComponent,
    ObservationPopUpDialogData,
} from './observation-pop-up/observation-pop-up.component';
import {
    ObservationReviewComponent,
    ObservationReviewDialogData,
} from './observation-review/observation-review.component';
import { ObservationExtraOptions } from './observation/observation-extra-options.model';

/** usable only from coaching-detail route */
@Injectable({ providedIn: 'root' })
export class CoachingObservationDialogService {
    private _refReview: CmpsDialogRef<ObservationReviewComponent, any>;
    private _refPopUp: CmpsDialogRef<ObservationPopUpComponent, any>;

    constructor(
        private readonly _router: Router,
        private readonly _dialog: CmpsDialog,
        private readonly _switchboardService: CoachingSwitchboardService,
    ) {}

    /** TODO replace with cmpsdialog */
    openReview(
        observationId: number,
        readOnly: boolean,
        focus: FocusDto,
        isLinkMode: boolean,
    ): void {
        // maybe dismiss should return a promise so that the new popup can open after/if the current one closes
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._refPopUp && this._refPopUp.close();

        this._switchboardService.pillarTwo.sbSwitchTab(TabRefId.OBSERVATIONS);

        if (!this._refReview) {
            const data: ObservationReviewDialogData = {
                options: {
                    // removed -- userId: this._parseCurrentUserId(),
                    observationId,
                    inReview: readOnly,
                    focus,
                    isLinkMode,
                },
                observationIdChange: (nextId) => {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises
                    this._switchboardService.pillarTwo
                        .sbSetStatuses([
                            {
                                which: TabRefId.OBSERVATIONS,
                                id: nextId,
                                type: undefined,
                                identifier: undefined,
                                active: true,
                            },
                        ])
                        .then(() => {
                            this._switchboardService.pillarTwo.sbScrollIntoView(
                                TabRefId.OBSERVATIONS,
                                nextId,
                            );
                        });
                },
            };

            this._refReview = this._dialog.open(ObservationReviewComponent, {
                disableBackdrop: true,
                disableClose: true,
                movable: true,
                canClose: () => {
                    const ret =
                        this._switchboardService.observation.sbCanCancelEdit();
                    return ret instanceof Observable ? ret.toPromise() : ret;
                },
                data,
            });

            this._refReview.result
                .pipe(
                    tap(() => {
                        // reset
                        // eslint-disable-next-line @typescript-eslint/no-floating-promises
                        this._switchboardService.pillarTwo.sbSetStatuses([
                            {
                                which: undefined,
                                id: undefined,
                                type: undefined,
                                identifier: undefined,
                                active: undefined,
                            },
                        ]);
                    }),
                    takeUntilDestroy(this),
                    // eslint-disable-next-line no-return-assign
                    finalize(() => (this._refReview = null)),
                )
                .subscribe();
        }
    }

    openNew(opts: {
        focus?: FocusShallow;
        draftCommitmentId?: string;
        commitmentId?: number;
        extraOptions?: ObservationExtraOptions;
    }): void {
        // maybe dismiss should return a promise so that the new popup can open after/if the current one closes
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._refReview && this._refReview.close();

        const data: ObservationPopUpDialogData = {
            userId:
                opts.extraOptions?.performerId ?? this._parseCurrentUserId(),
            focus: opts.focus,
            draftCommitmentId: opts.draftCommitmentId,
            commitmentId: opts.commitmentId,
            extraOptions: opts.extraOptions,
        };

        this._refPopUp = this._dialog.open(ObservationPopUpComponent, {
            disableBackdrop: true,
            disableClose: true,
            movable: true,
            canClose: () => {
                const ret =
                    this._switchboardService.observation.sbCanCancelEdit();
                return ret instanceof Observable ? ret.toPromise() : ret;
            },
            data,
        });

        this._refPopUp.result
            .pipe(
                takeUntilDestroy(this),
                // eslint-disable-next-line no-return-assign
                finalize(() => (this._refPopUp = null)),
            )
            .subscribe();
    }

    private _parseCurrentUserId(): number {
        const m = /.*\/coaching-detail\/(\d+).*/.exec(this._router.url);
        return m.length === 2 ? parseInt(m[1]) : null;
    }
}
